import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { genAttributes } from '@onward-delivery/core';
import { DROPOFF_TYPES } from '@/components/ShipmentForm/constants/dropoffOptions';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import { toNational, toE164 } from '@/utilities/formatPhoneNumber';
import { Header1, Body1, Tag, Step, SubStepDesktop, SubStepMobile, Anchor, Header2 } from './blocks';
import { formatInTimeZone } from 'date-fns-tz';
import { asDateInTZ } from '@/utilities/convertToISO';
import RevertDeliveryModal from '../modals/RevertDeliveryModal';
import { DELIVERY } from './itemEvents';

export default function Complete({
    isDesktop,
    isLoading,
    warehouse,
    before,
    after,
    order,
    callbacks,
    tz,
    readOnly,
    lastEvent,
}) {
    const { full_address, location_type, location } = genAttributes(order);

    // Service orders reuse the delivery path exactly — only the user-facing labels change.
    const isService = order.order_type === 'service';

    const [revertOpen, setRevertOpen] = useState(false);
    const isInEndState = order.event_state === 'END';
    const canRevert = !readOnly && isInEndState;
    const showExceptionCta = !readOnly && !isInEndState;

    // All active route mappings for this order — flipped to duplicate_override='DUPLICATE'
    // on a Complete attempt so the order is fully released and can be routed fresh.
    // Spans every active row (PICKUP+DROPOFF on different routes, or a single FULL mapping).
    const activeMappingIds = useMemo(
        () =>
            (order?.routes || [])
                .filter((m) => m?.mapping_id && (m?.duplicate_override == null || m?.duplicate_override === 'DISCARD'))
                .map((m) => m.mapping_id),
        [order?.routes]
    );

    const isE2E = [order.shipper_rate_override, order.carrier_rate_override].some(
        (override) => override?.override_type === 'E2E'
    );
    const hasMiddleMileReturnLeg =
        !!order.has_middle_mile_origin && (order.order_type === 'return' || order.order_type === 'wh_return') && !isE2E;

    const STEPS = useMemo(() => {
        return [
            {
                description: `1. ${isService ? 'Complete Service' : 'Deliver items'}`,
                complete: ({ order }) => {
                    return !!order.ltl_dropoff_arrival;
                },
                ...(!readOnly
                    ? {
                          cta: `${isService ? 'Complete Service' : 'Deliver items'}`,
                          callback: ({ order }) => {
                              const NOW = new Date();
                              const prompt = (
                                  <>
                                      <Grid
                                          container
                                          direction="row"
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          {isDesktop ? (
                                              <Body1>
                                                  {isService
                                                      ? 'Check each item as serviced.'
                                                      : 'Check each item as delivered.'}
                                              </Body1>
                                          ) : (
                                              <Header1>{isService ? 'Complete Service' : 'Deliver items'}</Header1>
                                          )}
                                      </Grid>
                                  </>
                              );

                              const callback = (orderUpdates, itemTrackingEvents) =>
                                  callbacks.upsertEvent({
                                      order: {
                                          ltl_dropoff_arrival: NOW.toISOString(),
                                          ...(orderUpdates || {}),
                                      },
                                      trackingEvents: itemTrackingEvents || [],
                                  });

                              callbacks.openChecklist(
                                  {
                                      title: isService ? 'Complete Service' : 'Deliver Items',
                                      content: prompt,
                                      documents: [],
                                      signatures: [],
                                      timestamps: [
                                          {
                                              field: 'ltl_dropoff_arrival',
                                              name: 'Arrival Time',
                                              required: true,
                                          },
                                      ],
                                      // DELIVERY inverts for return items — see itemEvents.js.
                                      ...DELIVERY,
                                      itemsHelperText: 'Only checked items are recorded.',
                                  },
                                  callback
                              );
                          },
                          ...(showExceptionCta
                              ? {
                                    secondaryCta: 'Add Exception',
                                    secondaryCallback: () => {
                                        callbacks.createException({
                                            type: 'DROPOFF',
                                            action: 'CD_COMPLETE:ATTEMPT',
                                            attempt_mapping_ids: activeMappingIds,
                                        });
                                    },
                                }
                              : {}),
                      }
                    : {}),
            },
            {
                description: `2. ${isService ? 'Upload Proof of Service documents' : 'Upload proof of delivery documents'}`,
                complete: ({ order, before, after }) => {
                    return !!order.pod?.length;
                },
                ...(!readOnly
                    ? {
                          cta: 'Upload POD',
                          callback: () => {
                              callbacks.gotoFileTab();
                          },
                          ...(showExceptionCta
                              ? {
                                    secondaryCta: 'Add Exception',
                                    secondaryCallback: ({ order, before, after }) => {
                                        callbacks.createException({
                                            type: 'DROPOFF',
                                            action: 'CD_COMPLETE:ATTEMPT',
                                            attempt_mapping_ids: activeMappingIds,
                                        });
                                    },
                                }
                              : {}),
                      }
                    : {}),
            },
            {
                description: `3. Mark order as '${isService ? 'Service Complete' : 'Delivered'}'`,
                complete: ({ order, before, after }) => {
                    const isReturn = ['return', 'wh_return'].includes(order.order_type);
                    const isMM = !!order.has_middle_mile_origin;
                    return (
                        !!after && ((isReturn && isMM) || ['complete', 'routedComplete'].includes(order.order_status))
                    );
                },
                ...(!readOnly
                    ? {
                          cta: isService ? 'Mark Service Complete' : 'Mark Delivered',
                          ...(showExceptionCta
                              ? {
                                    secondaryCta: 'Add Exception',
                                    secondaryCallback: ({ order, before, after }) => {
                                        callbacks.createException({
                                            type: 'DROPOFF',
                                            action: 'CD_COMPLETE:ATTEMPT',
                                            attempt_mapping_ids: activeMappingIds,
                                        });
                                    },
                                }
                              : {}),
                          callback: ({ order, before, after }) => {
                              const NOW = new Date();
                              const prompt = (
                                  <>
                                      <Grid
                                          direction="row"
                                          container
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          <Body1>{`Are you sure you want to mark the order as '${
                                              isService ? 'Service Complete' : 'Delivered'
                                          }'?`}</Body1>
                                      </Grid>
                                      <Grid direction="row" container>
                                          <Body1>
                                              {hasMiddleMileReturnLeg
                                                  ? 'This step will let the middle mile carrier know that the order is ready to be picked up from their warehouse.'
                                                  : isService
                                                  ? 'This step will close out the order and let the customer and shipper know the service has been completed.'
                                                  : 'This step will close out the order and let the customer and shipper know the delivery has been completed.'}
                                          </Body1>
                                      </Grid>
                                  </>
                              );
                              const callback = (orderUpdates = {}) =>
                                  callbacks.upsertEvent({
                                      event: {
                                          ...(after?.event_id
                                              ? { event_id: after.event_id, action: after.action }
                                              : {
                                                    action: `CD_COMPLETE:${
                                                        hasMiddleMileReturnLeg ? 'RETURN_TO_SENDER' : 'SUCCESS'
                                                    }`,
                                                }),
                                          order_id: order.order_id,
                                          delivery_date: orderUpdates?.completion_time || NOW.toISOString(),
                                      },
                                      order: {
                                          ...(hasMiddleMileReturnLeg
                                              ? {}
                                              : {
                                                    order_status: 'complete',
                                                    completion_time: NOW.toISOString(),
                                                    completion_source: COMPLETION_SOURCES.WEB,
                                                }),
                                          ...orderUpdates,
                                      },
                                  });

                              callbacks.openChecklist(
                                  {
                                      title: isService ? 'Complete Service' : 'Deliver Order',
                                      content: prompt,
                                      documents: [],
                                      signatures: [],
                                      timestamps: [
                                          { field: 'completion_time', name: 'Completion Time', required: true },
                                      ],
                                  },
                                  callback
                              );
                          },
                      }
                    : {}),
            },
        ];
    }, [isDesktop, full_address, location_type, location, order]);

    const isComplete = useMemo(() => {
        return STEPS.map((step) => step.complete({ order, before, after }));
    });

    const overrideComplete = useMemo(() => {
        return (
            !!after &&
            (['complete', 'routedComplete'].includes(order.order_status) || lastEvent.event_id !== after.event_id)
        );
    }, [before, after, order, lastEvent]);

    const inProgressIdx = useMemo(() => {
        return isComplete.findIndex((x) => !x);
    }, [isComplete]);

    const allComplete = isComplete.every((x) => x);
    const inProgress = !!before && !(allComplete || overrideComplete);
    const isReturn = ['return', 'wh_return'].includes(order.order_type);

    return (
        <>
            <Step
                isDesktop={isDesktop}
                isComplete={allComplete || overrideComplete}
                inProgress={inProgress}
                isLast={true}
                label={() => {
                    return (
                        <Grid
                            direction="row"
                            container
                            css={css`
                                align-items: center;
                            `}
                        >
                            <Grid
                                direction="column"
                                css={css`
                                    margin-right: 8px;
                                `}
                            >
                                <Header1>{isService ? 'Service Complete' : isReturn ? 'Returned' : 'Delivered'}</Header1>
                            </Grid>
                            {order.ltl_dropoff_arrival ? (
                                <Grid
                                    direction="column"
                                    css={css`
                                        margin-right: 8px;
                                    `}
                                >
                                    <Tag>
                                        <Body1
                                            css={css`
                                                margin-right: 20px;
                                            `}
                                        >
                                            {`Arrived: ${
                                                order.ltl_dropoff_arrival
                                                    ? formatInTimeZone(
                                                          order.ltl_dropoff_arrival,
                                                          tz,
                                                          'EEE, MMM dd, yyyy h:mm a zzz'
                                                      )
                                                    : 'Unknown'
                                            }`}
                                        </Body1>
                                        {!readOnly && (
                                            <Anchor onClick={() => STEPS[0]?.callback({ order, before, after })}>
                                                <Header1>Edit</Header1>
                                            </Anchor>
                                        )}
                                    </Tag>
                                </Grid>
                            ) : null}
                            <Grid
                                direction="column"
                                css={css`
                                    margin-right: 8px;
                                `}
                            >
                                {!!after ? (
                                    <Tag>
                                        <Body1
                                            css={css`
                                                margin-right: 20px;
                                            `}
                                        >
                                            {`Completed: ${
                                                order.completion_time
                                                    ? formatInTimeZone(
                                                          order.completion_time,
                                                          tz,
                                                          'EEE, MMM dd, yyyy h:mm a zzz'
                                                      )
                                                    : 'Unknown'
                                            }`}
                                        </Body1>
                                        {!readOnly && (
                                            <Anchor onClick={() => STEPS[2]?.callback({ order, before, after })}>
                                                <Header1>Edit</Header1>
                                            </Anchor>
                                        )}
                                    </Tag>
                                ) : (
                                    <Tag>
                                        <Body1>{`Scheduled Delivery: ${
                                            before?.est_delivery_date ||
                                            (order.order_type === 'return' ? order.pickup_date : order.delivery_date)
                                                ? formatInTimeZone(
                                                      asDateInTZ(
                                                          before?.est_delivery_date ||
                                                              (order.order_type === 'return'
                                                                  ? order.pickup_date
                                                                  : order.delivery_date),
                                                          tz
                                                      ),
                                                      tz,
                                                      'EEE, MMM dd yyyy'
                                                  )
                                                : 'Pending'
                                        }`}</Body1>
                                    </Tag>
                                )}
                            </Grid>
                            {canRevert ? (
                                <Grid
                                    direction="column"
                                    css={css`
                                        margin-right: 8px;
                                    `}
                                >
                                    <Anchor onClick={() => setRevertOpen(true)}>Revert delivery</Anchor>
                                </Grid>
                            ) : null}
                        </Grid>
                    );
                }}
            >
                {() => (
                    <>
                        {STEPS.map(({ description, cta, secondaryCta, callback, secondaryCallback }, idx) => {
                            const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;

                            return (
                                <SubStep
                                    key={idx}
                                    isComplete={isComplete[idx]}
                                    inProgress={inProgressIdx === idx}
                                    isLoading={isLoading}
                                    isLast={idx === STEPS.length - 1}
                                    description={description}
                                    cta={cta}
                                    secondaryCta={secondaryCta}
                                    secondaryOrange={true}
                                    callback={
                                        callback
                                            ? () => {
                                                  callback({ before, after, order });
                                              }
                                            : null
                                    }
                                    secondaryCallback={
                                        secondaryCallback
                                            ? () => {
                                                  secondaryCallback({ before, after, order });
                                              }
                                            : null
                                    }
                                />
                            );
                        })}
                    </>
                )}
            </Step>
            <RevertDeliveryModal
                open={revertOpen}
                onClose={() => setRevertOpen(false)}
                onConfirm={({ reason, clearDocuments }) => {
                    return callbacks.upsertEvent({
                        event: {
                            order_id: order.order_id,
                            action: 'END:REVERT',
                            notes: reason,
                        },
                        order: {
                            order_status: 'inProgress',
                            completion_time: null,
                            completion_source: null,
                            ltl_dropoff_arrival: null,
                            ...(clearDocuments ? { pod: null, delivery_photo: null } : {}),
                        },
                    });
                }}
                loading={isLoading}
            />
        </>
    );
}
